import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import { Link } from 'react-router-dom'
import { Plug, Plus, Play, Settings, Database, Globe, Mail, ArrowRight, Trash2, Loader2 } from 'lucide-react'
import ProductGuide from '../components/ProductGuide'
import ConnectorGrid from '../components/ConnectorGrid'

const SOURCE_TYPES = [
  { type: 'rest_api', label: 'REST API', icon: Globe },
  { type: 'soap', label: 'SOAP Service', icon: Globe },
  { type: 'database', label: 'Database', icon: Database },
  { type: 'webhook', label: 'Webhook', icon: Plug },
]

const TARGET_TYPES = [
  { type: 'webhook', label: 'Webhook', icon: Globe },
  { type: 'queue', label: 'Message Queue', icon: Database },
  { type: 'api', label: 'REST API', icon: Globe },
]

export default function AdapterManager() {
  const [adapters, setAdapters] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [runResult, setRunResult] = useState({}) // id -> {busy, out, err}
  const load = () => axios.get('/api/v1/middleware/adapters').then(r => setAdapters(r.data.adapters || [])).catch(() => {})
  useEffect(() => { load() }, [])
  const [form, setForm] = useState({
    name: '', sourceType: 'rest_api', sourceConfig: {}, transformRules: [], targetType: 'webhook', targetConfig: {}
  })

  const createAdapter = async (e) => {
    e.preventDefault()
    try {
      await axios.post('/api/v1/middleware/adapters', form)
      toast.success('Adapter created!')
      setShowForm(false); load()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not create adapter')
    }
  }

  const runAdapter = async (id) => {
    setRunResult(s => ({ ...s, [id]: { busy: true } }))
    try {
      const res = await axios.post(`/api/v1/middleware/adapters/${id}/run`, {})
      setRunResult(s => ({ ...s, [id]: { out: JSON.stringify(res.data.output ?? res.data, null, 2) } }))
    } catch (err) {
      setRunResult(s => ({ ...s, [id]: { err: err.response?.data?.error || 'Run failed' } }))
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Middleware Replacement</h2>
          <p className="text-gray-500 mt-1">Connect legacy systems to AI-native pipelines</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2">
          <Plus size={18} /> New Adapter
        </button>
      </div>

      <ProductGuide product="middleware" collapsible defaultOpen={false} />

      {/* Your adapters — run the ones you've built */}
      {adapters.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Your adapters <span className="text-sm font-normal text-gray-400">({adapters.length})</span></h3>
          <div className="space-y-2">
            {adapters.map(a => {
              const rr = runResult[a.id] || {}
              return (
                <div key={a.id} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary-500/10 shrink-0"><Plug size={15} className="text-primary-600" /></div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{a.name}</p>
                      <p className="text-xs text-gray-400">{a.source_type} → webhook · created {new Date(a.created_at).toLocaleDateString()}</p>
                    </div>
                    <button onClick={() => runAdapter(a.id)} disabled={rr.busy} className="btn-primary text-sm px-4 py-1.5 flex items-center gap-1.5 disabled:opacity-60">
                      {rr.busy ? <Loader2 className="animate-spin" size={14} /> : <Play size={14} />} Run
                    </button>
                  </div>
                  {rr.out && <pre className="mt-2 text-xs bg-gray-50 border border-gray-100 rounded p-2 whitespace-pre-wrap max-h-40 overflow-y-auto">{rr.out}</pre>}
                  {rr.err && <p className="mt-2 text-xs text-red-600">{rr.err}</p>}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Connect a data source — click a logo to feed your instances */}
      <div className="card">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Database size={18} className="text-primary-600" /> Connect a data source</h3>
          <Link to="/instances" className="text-sm text-primary-600 hover:underline">Manage in Instances →</Link>
        </div>
        <p className="text-sm text-gray-500 mb-4">Click a system to connect it — its content feeds your workspace’s knowledge (used by every service). This is different from the webhook <b>adapters</b> below, which reshape inbound payloads.</p>
        <ConnectorGrid onDone={() => toast.success('Source connected — indexing in the background')} />
      </div>

      {showForm && (
        <div className="card max-w-3xl">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Adapter</h3>
          <form onSubmit={createAdapter} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Adapter Name</label>
              <input type="text" className="input" required placeholder="e.g., CRM-to-Salesforce Bridge"
                value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                <select className="input" value={form.sourceType} onChange={e => setForm({...form, sourceType: e.target.value})}>
                  {SOURCE_TYPES.map(s => <option key={s.type} value={s.type}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target</label>
                <select className="input" value={form.targetType} onChange={e => setForm({...form, targetType: e.target.value})}>
                  {TARGET_TYPES.map(t => <option key={t.type} value={t.type}>{t.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Source URL / Endpoint</label>
              <input type="url" className="input" placeholder="https://legacy-api.company.com/v1/data"
                onChange={e => setForm({...form, sourceConfig: { ...form.sourceConfig, url: e.target.value }})} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Transform Rules (JSON)</label>
              <textarea className="input h-24 font-mono text-xs" placeholder='[{"type":"map","mapping":{"name":"customer_name"}}]'
                onChange={e => {
                  try { setForm({...form, transformRules: JSON.parse(e.target.value)}) } catch {}
                }} />
            </div>
            <div className="flex gap-2">
              <button type="submit" className="btn-primary">Create Adapter</button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-2 gap-6">
        {adapters.map(adapter => (
          <div key={adapter.id} className="card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Plug className="text-primary-500" size={24} />
                <div>
                  <h3 className="font-semibold text-gray-900">{adapter.name}</h3>
                  <p className="text-xs text-gray-500">{adapter.sourceType} → {adapter.targetType}</p>
                </div>
              </div>
              <span className="px-2 py-1 bg-green-500/15 text-green-600 text-xs rounded-full">{adapter.status}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
              <Globe size={14} />
              <span className="truncate">{adapter.sourceConfig?.url || 'No URL configured'}</span>
            </div>
            <div className="flex gap-2">
              <button onClick={() => runAdapter(adapter.id)} className="btn-primary text-sm flex items-center gap-1">
                <Play size={14} /> Run
              </button>
              <button className="btn-secondary text-sm flex items-center gap-1">
                <Settings size={14} /> Edit
              </button>
              <button className="p-2 text-gray-400 hover:text-red-500 transition-colors">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
